/**
 * 弹窗公告组件
 * 显示未读的弹窗公告，支持逐条展示和确认
 */
import { Dialog } from '@base-ui-components/react/dialog';
import { Bell } from 'lucide-react';
import { cn } from '@/utils/cn';
import Button from './Button';
import { usePopupAnnouncements } from '@/hooks/useAnnouncement';
import { useAuth } from '@/contexts/GlobalProvider';

/**
 * 弹窗公告组件
 * 自动获取未读公告并显示，支持需要确认和不需要确认两种模式
 * 只有在用户已登录时才会请求公告接口
 */
export default function AnnouncementPopup() {
  // 只有已登录才启用公告请求，避免未登录时触发 tokenError
  const { isAuthenticated } = useAuth();
  const {
    currentAnnouncement,
    hasAnnouncements,
    remainingCount,
    markCurrentAsRead,
    skipCurrent,
    loading,
  } = usePopupAnnouncements(isAuthenticated);

  // 无公告或加载中时不渲染
  if (loading || !hasAnnouncements || !currentAnnouncement) {
    return null;
  }

  const handleConfirm = async () => {
    await markCurrentAsRead();
  };

  const handleSkip = () => {
    skipCurrent();
  };

  return (
    <Dialog.Root open={hasAnnouncements}>
      <Dialog.Portal>
        {/* 遮罩层 */}
        <Dialog.Backdrop
          className={cn(
            'fixed inset-0 min-h-dvh bg-black/50 z-[1000]',
            'transition-opacity duration-300',
            'data-[starting-style]:opacity-0',
            'data-[ending-style]:opacity-0'
          )}
        />

        {/* 弹窗内容 */}
        <Dialog.Popup
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[85%] max-w-[340px]',
            'bg-bg-card rounded-2xl',
            'shadow-xl',
            'transition-all duration-300 ease-out',
            'data-[starting-style]:opacity-0 data-[starting-style]:scale-95',
            'data-[ending-style]:opacity-0 data-[ending-style]:scale-95',
            'outline-none z-[1001]'
          )}
        >
          {/* 顶部图标装饰 */}
          <div className="flex justify-center -mt-6">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg">
              <Bell size={24} className="text-white" />
            </div>
          </div>

          {/* 内容区域 */}
          <div className="px-5 pt-4 pb-5">
            {/* 标题 */}
            <Dialog.Title className="text-lg font-semibold text-text-primary text-center mb-3">
              {currentAnnouncement.title}
            </Dialog.Title>

            {/* 公告内容 */}
            <Dialog.Description
              className={cn(
                'text-sm text-text-secondary leading-relaxed',
                'max-h-[40vh] overflow-y-auto',
                'whitespace-pre-wrap break-words'
              )}
            >
              {currentAnnouncement.content}
            </Dialog.Description>

            {/* 剩余公告提示 */}
            {remainingCount > 1 && (
              <div className="text-xs text-text-secondary text-center mt-3">
                还有 {remainingCount - 1} 条公告
              </div>
            )}

            {/* 按钮区域 */}
            <div className="mt-5 flex gap-3">
              {currentAnnouncement.requireConfirm ? (
                // 需要确认模式：只显示确认按钮
                <Button variant="primary" block onClick={handleConfirm}>
                  {currentAnnouncement.confirmButtonText || '我知道了'}
                </Button>
              ) : (
                // 不需要确认模式：显示跳过和下一条按钮
                <>
                  <Button variant="default" block onClick={handleSkip}>
                    跳过
                  </Button>
                  <Button variant="primary" block onClick={handleConfirm}>
                    {remainingCount > 1 ? '下一条' : '关闭'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
